// An entrant's details as an incident's read-only view shows them, shared so the
// Feedback page's linked entrant reads exactly the same: a one-line summary beside
// an eye icon that opens everything else (emergency contact, medical information,
// screening), and the watch-list alert when they were flagged at screening.
//
// `showScreening` is the caller's EventParticipants.viewScreening (or equivalent).
// The server sends no screening fields to a reader without it (core/clinical.py),
// so an absent status must never fall back to the default: absent means "not shown
// to you", which is not the same as "Not Required".
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Tooltip, Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { SCREENING_STATUSES, SCREENING_STATUS_COLOR, WATCH_LIST_STATUS } from "../constants";
import { useFullScreenDialog } from "../useFullScreenDialog";

/** The summary line, in the order the incident's patient summary uses. */
export const participantSummary = (p) => [
  p.name,
  p.race_number && `#${p.race_number}`,
  (p.age !== "" && p.age != null) && `${p.age} yrs`,
  p.sex,
  p.race,
  p.phone,
  p.email,
].filter(Boolean).join("  ·  ");

export function ViewParticipantButton({ onClick, size = "small" }) {
  return (
    <Tooltip title="View participant details">
      <IconButton size={size} onClick={onClick}>
        <VisibilityIcon fontSize={size === "small" ? "small" : undefined} />
      </IconButton>
    </Tooltip>
  );
}

/** The flag raised at pre-event screening, with its reason. Renders nothing otherwise. */
export function WatchListAlert({ participant, showScreening }) {
  if (!showScreening || participant?.screening_status !== WATCH_LIST_STATUS) return null;
  return (
    <Alert severity="error" icon={<WarningAmberIcon />}>
      <Typography variant="subtitle2" gutterBottom>
        On the watch list — flagged at medical screening
      </Typography>
      {/* pre-wrap so the line breaks in the screening notes survive */}
      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
        {participant.watch_list_notes
          || "No watch list notes were recorded — check with the screening team."}
      </Typography>
    </Alert>
  );
}

/** Read-only participant details (incl. fields not on the incident form). Open while `participant` is set. */
export default function ParticipantDetailsDialog({ participant, onClose, showScreening }) {
  const fullScreen = useFullScreenDialog();
  return (
    <Dialog open={!!participant} onClose={onClose} fullWidth maxWidth="sm" fullScreen={fullScreen}>
      <DialogTitle>{participant?.name || "Participant"}</DialogTitle>
      <DialogContent>
        {participant && (
          <Box component="dl" sx={{ display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 2, rowGap: 1, m: 0 }}>
            {[
              ["Name", participant.name],
              ["Race", participant.race],
              ["Race number", participant.race_number],
              ["Age", participant.age],
              ["Date of birth", participant.date_of_birth],
              ["Sex", participant.sex],
              ["Phone", participant.phone],
              ["Email", participant.email],
              ["Emergency contact", participant.emergency_contact_name],
              ["Emergency phone", participant.emergency_contact_phone],
              ["Medical information", participant.medical_information],
              ...(!showScreening ? [] : [
                ["Screening", (() => {
                  const s = participant.screening_status || SCREENING_STATUSES[0];
                  return <Chip size="small" label={s} color={SCREENING_STATUS_COLOR[s] || "default"} />;
                })()],
                ["Notes", participant.notes],
                // Only while they are on the watch list — the text can outlive the
                // status, but it stops being what a responder needs to read.
                ...(participant.screening_status === WATCH_LIST_STATUS
                  ? [["Watch list notes", participant.watch_list_notes]] : []),
              ]),
            ].map(([label, value]) => (
              <Box key={label} sx={{ display: "contents" }}>
                <Box component="dt" sx={{ fontWeight: 600 }}>{label}</Box>
                {/* pre-wrap so the line breaks in medical information / notes survive */}
                <Box component="dd" sx={{ m: 0, whiteSpace: "pre-wrap" }}>{value ?? "—"}</Box>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
